/**
 * [2026-09-12 / 2026-09-13 하이브리드 재설계] A/S 공용 UI — 고객(MyAs)·관리자(AdminAs) 재사용.
 *   AsDomainBadge(안전/환경=제품 배지) · AsStatusBadge · AsStepper(활성 단계 진행 표시, 열람용)
 *   · AsHero · AsTimeline(공유 이력) · AsStageEditor(설정: 원 클릭=활성 토글/드래그=순서) · AsStageSlider(진행: 핸들 이동→상위 팝업).
 *
 * ★단계 축(2026-09-13): 안전/환경(도메인) 아님 → '케이스별 단계 구성(stage_plan)'. 라벨/설명/아이콘은 범용(asMeta).
 * ★생애주기: 설정(구성 편집·확정 전) → [구성 확정=진행 시작]으로 구성 잠금(불변, BPM 원리) → 진행(슬라이더로 현재 단계 이동).
 *   진행 이동은 상위 팝업에서 내용/사유 입력 후 [적용]해야 실제 반영(뒤로=되돌림=사유 필수). 재구성은 사유 기록 후 잠금 해제.
 */
import { Fragment, useRef, useState } from 'react';
import { AS_STEP, AS_DOMAIN, asFlowIndex, asLabel, asDesc, asIcon, asHeadline, type AsStageItem } from './asMeta';
import s from './As.module.scss';

export interface AsLogRow {
	id: number; from_status: string | null; to_status: string | null; note: string | null;
	actor_type: string; actor_name: string | null; created_at: string;
}

function StepSvg({ d }: { d: string }) {
	return <svg viewBox="0 0 24 24" fill="none" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round"><path d={d} /></svg>;
}
// [2026-09-13 사용자] 자물쇠 = 가독성 있게(채운 배지 + 흰 자물쇠). 완료·잠금 단계 표시.
function LockSvg() {
	return <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2a5 5 0 0 0-5 5v3H6a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7a2 2 0 0 0-2-2h-1V7a5 5 0 0 0-5-5zm-3 5a3 3 0 0 1 6 0v3H9V7zm3 7a1.6 1.6 0 0 1 .8 3v2.2h-1.6V17a1.6 1.6 0 0 1 .8-3z" /></svg>;
}

export function AsDomainBadge({ domain }: { domain: string }) {
	const cls = domain === 'SAFETY' ? s.bSafety : s.bEnv;
	return <span className={`${s.badge} ${cls}`}>{AS_DOMAIN[domain]?.label || domain}</span>;
}

export function AsStatusBadge({ status }: { status: string; domain?: string }) {
	const cls = status === 'COMPLETED' ? s.statDone : status === 'CANCELED' ? s.statCancel
		: status === 'ON_HOLD' ? s.statHold : status === 'RECEIVED' ? s.stat : s.statActive;
	return <span className={`${s.badge} ${cls}`}>{asLabel(status)}</span>;
}

/** 가로 진행 스테퍼(열람용) — 케이스의 '활성 단계'만 순서대로. 스텝 클릭 시 하단 설명줄 갱신. 취소/보류는 안내 바. */
export function AsStepper({ status, steps }: { status: string; steps: string[] }) {
	const list = steps && steps.length ? steps : ['RECEIVED'];
	const cur = list.indexOf(status);
	const isDone = status === 'COMPLETED';
	const defaultPeek = isDone ? list.length - 1 : Math.max(0, cur);
	const [peek, setPeek] = useState<number | null>(null);
	const shown = peek == null ? defaultPeek : peek;

	if (status === 'CANCELED' || status === 'ON_HOLD') {
		return (
			<div className={s.exBar}><AsStatusBadge status={status} />
				{status === 'CANCELED' ? '취소된 신청입니다.' : '현재 보류 상태입니다. 담당자 확인 후 재개됩니다.'}</div>
		);
	}
	return (
		<div>
			<div className={s.stepper}>
				{list.map((st, i) => {
					const done = i < cur || isDone;
					const current = i === cur && !isDone;
					const cls = `${current ? s.current : ''} ${done ? s.done : ''} ${shown === i ? s.peek : ''}`;
					return (
						<Fragment key={st}>
							{i > 0 && <div className={`${s.stepLine} ${i <= cur || isDone ? s.doneLine : ''}`} />}
							<button type="button" className={`${s.step} ${cls}`} onClick={() => setPeek(i)} aria-label={asLabel(st)}>
								<div className={s.stepDot}><StepSvg d={asIcon(st)} /></div>
								<div className={s.stepLabel}>{asLabel(st)}</div>
							</button>
						</Fragment>
					);
				})}
			</div>
			<div className={s.stepDesc} key={shown}><b>{asLabel(list[shown])}</b> — {asDesc(list[shown])}</div>
		</div>
	);
}

const dt = (v: string) => String(v).slice(0, 16).replace('T', ' ');
const ymd = (v: string) => String(v).slice(0, 10);

/** A/S 히어로 — 현재 단계를 큰 아이콘 + 얇은 대형 타이포로. 메타(N/총 활성단계·도메인 배지) + 상태 문구 + 제품·접수일. */
export function AsHero({ status, domain, steps, product, createdAt, locked }:
	{ status: string; domain?: string; steps: string[]; product: string; createdAt: string; locked?: boolean }) {
	const total = steps.length || 1;
	const exception = status === 'CANCELED' || status === 'ON_HOLD';
	// [2026-09-13 사용자] 미확정(locked===false)=진행 전 — 접수조차 '진행중' 아님. '접수 완료 · 담당자 확인 중'으로 표기.
	const setup = locked === false && !exception;
	const pos = steps.indexOf(status);
	const stepNo = status === 'COMPLETED' ? total : pos >= 0 ? pos + 1 : 0;
	return (
		<div className={s.hero}>
			<div className={`${s.heroIco} ${exception ? s.heroIcoEx : ''}`}>
				<svg viewBox="0 0 24 24" fill="none" strokeWidth={1.4} strokeLinecap="round" strokeLinejoin="round"><path d={asIcon(setup ? 'RECEIVED' : status)} /></svg>
			</div>
			<div className={s.heroBody}>
				<div className={s.heroMeta}>{setup ? '접수 완료 · 진행 준비 중' : (exception ? asLabel(status) : `${stepNo} / ${total} 단계`)} · {AS_DOMAIN[domain || '']?.label || domain}</div>
				<div className={s.heroTitle}>{setup ? '접수 완료 · 담당자 확인 중' : asHeadline(status)}</div>
				<div className={s.heroSub}>{product} · 접수 {ymd(createdAt)}{setup ? ' · 진행 단계는 담당자 확정 후 시작됩니다' : ''}</div>
			</div>
		</div>
	);
}

/** 세로 노드 타임라인(최신 위). rows 서버 오름차순 → 역순. 라벨 범용(asMeta). 되돌림(↩) 감지 표기.
 *  [2026-09-13 사용자] 이력이 길어질 수 있어 최근 N개만 표시하고 '더 보기'로 펼침(페이징 대신 — 시간흐름 유지). */
const TL_CAP = 6;
export function AsTimeline({ rows }: { rows: AsLogRow[]; domain?: string }) {
	const [expanded, setExpanded] = useState(false);
	if (!rows || rows.length === 0) return <div className={s.empty}>진행 이력이 없습니다.</div>;
	const all = [...rows].reverse();
	const hidden = Math.max(0, all.length - TL_CAP);
	const items = expanded ? all : all.slice(0, TL_CAP);
	return (
		<><div className={s.timeline}>
			{items.map((r, i) => {
				const admin = r.actor_type === 'ADMIN';
				const stage = r.to_status || r.from_status;
				const stageLabel = stage ? (AS_STEP[stage] ? asLabel(stage) : stage) : '메시지';
				const fi = r.from_status ? asFlowIndex(r.from_status) : -1;
				const ti = r.to_status ? asFlowIndex(r.to_status) : -1;
				const isRevert = fi >= 0 && ti >= 0 && ti < fi;
				return (
					<div className={s.tlRow} key={r.id}>
						<div className={s.tlRail}>
							<div className={`${s.tlDot} ${i === 0 ? s.tlDotActive : ''} ${admin ? s.tlDotAdmin : ''}`} />
							{i < items.length - 1 && <div className={s.tlLine} />}
						</div>
						<div className={s.tlBody}>
							<div className={s.tlHead}>
								<span className={admin ? s.tlStageAdmin : s.tlStage}>{stageLabel}</span>
								{isRevert && <span className={s.tlRevert}>↩ 되돌림({asLabel(r.from_status!)}→{asLabel(r.to_status!)})</span>}
								{r.note ? <> · {r.note}</> : null}
								<span className={`${s.tlWho} ${admin ? s.whoAdmin : s.whoUser}`}>{admin ? (r.actor_name || '관리자') : '고객'}</span>
							</div>
							<div className={s.tlTime}>{dt(r.created_at)}</div>
						</div>
					</div>
				);
			})}
		</div>
		{!expanded && hidden > 0 && (
			<button type="button" className={s.tlMore} onClick={() => setExpanded(true)}>이전 이력 {hidden}개 더 보기</button>
		)}
		</>
	);
}

/**
 * [2026-09-13 사용자 확정] 관리자 단계 구성 편집기 — '설정 모드'(확정 전)에서만 렌더.
 *   8단계 전체가 순서대로: 원 클릭 = 사용/미사용 토글 · 좌우 드래그 = 순서 변경. 활성(파란)만 고객에게 공지.
 *   확정되면(잠금) 상위가 이 편집기 대신 슬라이더를 렌더하므로 여기선 per-stage 잠금 없이 전체 편집.
 *   변경은 상위(onChange)에서 DB(/plan) 저장. 클릭 vs 드래그: 6px 초과=드래그(순서), 아니면 클릭(토글).
 */
export function AsStageEditor({ plan, onChange, disabled }:
	{ plan: AsStageItem[]; onChange: (next: AsStageItem[]) => void; disabled?: boolean }) {
	const [order, setOrder] = useState<AsStageItem[]>(plan);
	const trackRef = useRef<HTMLDivElement>(null);
	const drag = useRef<{ from: number; startX: number; moved: boolean } | null>(null);
	const [dragIdx, setDragIdx] = useState<number | null>(null);

	const planKey = plan.map((p) => p.key + (p.active ? '1' : '0')).join(',');
	const syncedKey = useRef(planKey);
	if (syncedKey.current !== planKey) { syncedKey.current = planKey; if (order.map((p) => p.key + (p.active ? '1' : '0')).join(',') !== planKey) setOrder(plan); }

	function idxFromX(clientX: number): number {
		const t = trackRef.current; if (!t) return 0;
		const nodes = Array.from(t.querySelectorAll<HTMLElement>('[data-se]'));
		let best = 0, bd = Infinity;
		nodes.forEach((el, i) => { const r = el.getBoundingClientRect(); const c = r.left + r.width / 2; const d = Math.abs(clientX - c); if (d < bd) { bd = d; best = i; } });
		return best;
	}
	// [2026-09-13 사용자 확정] 접수(RECEIVED)=A/S 시작점 → 항상 첫 단계·활성 고정(토글·드래그·재배치 불가). 그 이후 단계만 편집.
	function onDown(i: number, e: React.PointerEvent) {
		if (disabled || order[i].key === 'RECEIVED') return;
		(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
		drag.current = { from: i, startX: e.clientX, moved: false }; setDragIdx(i);
	}
	function onMove(e: React.PointerEvent) {
		const d = drag.current; if (!d) return;
		if (!d.moved && Math.abs(e.clientX - d.startX) > 6) d.moved = true;
		if (!d.moved) return;
		const to = Math.max(1, idxFromX(e.clientX));   // 접수(index 0) 앞으로는 못 감
		if (to !== d.from) {
			setOrder((prev) => { const a = [...prev]; const [m] = a.splice(d.from, 1); a.splice(to, 0, m); return a; });
			d.from = to; setDragIdx(to);
		}
	}
	function onUp(i: number) {
		const d = drag.current; drag.current = null; setDragIdx(null);
		if (!d) return;
		if (d.moved) onChange(order);            // 드래그 → 순서 저장
		else if (!disabled && order[i].key !== 'RECEIVED') toggle(i);   // 클릭 → 토글(접수 제외)
	}
	function toggle(i: number) {
		const next = order.map((p, j) => (j === i ? { ...p, active: !p.active } : p));
		setOrder(next); onChange(next);
	}

	return (
		<div>
			<div className={s.seHint}><b>접수</b>는 시작 단계로 고정입니다. 이후 단계의 원을 <b>클릭</b>=켜기/끄기 · 좌우 <b>드래그</b>=순서. <b>활성(파란) 단계만</b> 고객에게 공지됩니다. 아래 <b>[구성 확정]</b>을 눌러야 진행이 시작됩니다.</div>
			<div className={s.seTrack} ref={trackRef}>
				{order.map((p, i) => {
					const fixed = p.key === 'RECEIVED';
					return (
						<button type="button" data-se key={p.key}
							className={`${s.seNode} ${p.active ? s.seOn : s.seOff} ${fixed ? s.seFixed : ''} ${dragIdx === i ? s.seDrag : ''}`}
							style={{ touchAction: 'none', cursor: (disabled || fixed) ? 'default' : 'grab' }}
							onPointerDown={(e) => onDown(i, e)} onPointerMove={onMove} onPointerUp={() => onUp(i)}
							aria-pressed={p.active} aria-label={`${asLabel(p.key)} ${fixed ? '시작 고정' : (p.active ? '사용' : '미사용')}`}>
							<span className={s.seDot}><StepSvg d={asIcon(p.key)} /></span>
							<span className={s.seNum}>{i + 1}</span>
							<span className={s.seLabel}>{asLabel(p.key)}</span>
							<span className={s.seTag}>{fixed ? '시작·고정' : (p.active ? '사용' : '미사용')}</span>
						</button>
					);
				})}
			</div>
		</div>
	);
}

/**
 * [2026-09-13 사용자 확정] 관리자 진행 슬라이더 — '진행 모드'(구성 확정 후)에서만 렌더.
 *   활성 단계만 표시. 핸들을 드래그(또는 단계 클릭)해 목표 단계를 '지정'하면 상위(onPropose)가 팝업을 연다.
 *   ★슬라이더는 스스로 이동을 확정하지 않는다 — 팝업에서 내용/사유 입력 후 [적용]해야 실제 이동(DB). 취소 시 핸들 원위치.
 *   과거(완료) 단계는 자물쇠 배지로 표기. 뒤로 지정 = 되돌림(상위 팝업에서 사유 필수).
 */
export function AsStageSlider({ steps, status, onPropose, disabled }:
	{ steps: string[]; status: string; onPropose: (toKey: string) => void; disabled?: boolean }) {
	const list = steps && steps.length ? steps : ['RECEIVED'];
	const n = list.length;
	const curIdx = Math.max(0, list.indexOf(status));
	const isDone = status === 'COMPLETED';
	const trackRef = useRef<HTMLDivElement>(null);
	const [pos, setPos] = useState(curIdx);
	const dragging = useRef(false);

	const syncKey = status + '|' + list.join(',');
	const synced = useRef(syncKey);
	if (synced.current !== syncKey) { synced.current = syncKey; setPos(curIdx); }

	if (status === 'CANCELED' || status === 'ON_HOLD') {
		return <div className={s.exBar}><AsStatusBadge status={status} />{status === 'CANCELED' ? '취소된 신청입니다.' : '보류 상태입니다. 재개 시 진행됩니다.'}</div>;
	}

	function idxFromX(clientX: number): number {
		const t = trackRef.current; if (!t) return curIdx;
		const nodes = Array.from(t.querySelectorAll<HTMLElement>('[data-sl]'));
		let best = 0, bd = Infinity;
		nodes.forEach((el, i) => { const r = el.getBoundingClientRect(); const c = r.left + r.width / 2; const d = Math.abs(clientX - c); if (d < bd) { bd = d; best = i; } });
		return best;
	}
	function down(e: React.PointerEvent) { if (disabled) return; dragging.current = true; (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId); }
	function move(e: React.PointerEvent) { if (dragging.current) setPos(idxFromX(e.clientX)); }
	function up() { if (!dragging.current) return; dragging.current = false; const p = pos; setPos(curIdx); if (p !== curIdx) onPropose(list[p]); }
	const pct = (i: number) => (n <= 1 ? 50 : ((i + 0.5) / n) * 100);

	return (
		<div>
			<div className={s.slStages} ref={trackRef}>
				{list.map((st, i) => {
					const done = i < curIdx || isDone;
					const cur = i === curIdx && !isDone;
					return (
						<div data-sl key={st} className={`${s.slNode} ${done ? s.slDone : ''} ${cur ? s.slCur : ''}`}>
							{i > 0 && <span className={`${s.slLine} ${i <= curIdx || isDone ? s.slLineOn : ''}`} />}
							<span className={s.slDot}><StepSvg d={asIcon(st)} />{done && <span className={s.slLock}><LockSvg /></span>}</span>
							<span className={s.slLabel}>{asLabel(st)}</span>
						</div>
					);
				})}
			</div>
			<div className={s.slRail}>
				<div className={s.slTrack} />
				<div className={s.slFill} style={{ width: `${pct(pos)}%` }} />
				<div className={`${s.slThumb} ${pos < curIdx ? s.slThumbRev : ''}`} style={{ left: `${pct(pos)}%` }}
					onPointerDown={down} onPointerMove={move} onPointerUp={up} role="slider"
					aria-valuenow={pos} aria-valuemin={0} aria-valuemax={n - 1} aria-label="현재 단계 핸들" tabIndex={0} />
			</div>
			<div className={s.slHint}>아래 <b>핸들을 좌우로 옮기면</b> → 팝업에서 내용 입력·<b>[적용]</b>해야 실제 단계가 이동합니다 · 뒤로 = 되돌림(사유 필수)</div>
		</div>
	);
}
